import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LargeContainer from '@/components/front-office/containers/LargeContainer';
import { usePaymentConfirmedContext } from '@/contexts/PaymentConfirmedContext';
import { useOrdersContext } from '@/contexts/OrderContext';
import useIndexedDB from '@/hooks/useIndexedDB';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';

const PaymentForm = () => {
    const [formToken, setFormToken] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { paymentConfirmedObject, setPaymentConfirmedObject } = usePaymentConfirmedContext();
    const { cart, setCart, initializeCart } = useOrdersContext();
    const { deleteAllDBs, deleteCart } = useIndexedDB();

    const amount = searchParams.get('amount');
    const customerEmail = searchParams.get('customerEmail');
    const orderId = searchParams.get('orderId');
    const restaurantId = searchParams.get('restaurantId');
    const orderReference = searchParams.get('orderReference');
    const deliveryFees = searchParams.get('deliveryFees');

    // Récupérer le formToken pour le paiement
    useEffect(() => {
        if (orderId) {
            setPaymentConfirmedObject({ orderId, restaurantId, orderReference });
        }

        const fetchFormToken = async () => {
            try {
                const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/createPayment`, { 
                    amount, 
                    orderId: paymentConfirmedObject.orderId, 
                    customerEmail,
                    deliveryFees
                }, {
                    headers: { 
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*'
                    },
                    withCredentials: true
                });
                
                const token = response.data;
                setFormToken(token);

                // Ajouter un petit délai pour s'assurer que le DOM est prêt
                setTimeout(() => {
                    if (window.KR && document.getElementById('myPaymentForm')) {
                        window.KR.setFormToken(token);
                        window.KR.attachForm(document.getElementById('myPaymentForm'));
                    }
                }, 500); // Délai de 500ms pour garantir que l'élément est prêt
            } catch (error) {
                console.error('Erreur lors de la récupération du token de formulaire:', error);
            }
        };

        fetchFormToken();

        window.KR.onTransactionCreated((e) => {
            const handlePaymentSuccess = async () => {
                try {
                    // Confirmation du paiement et creation facture
                    const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/orders/confirmPayment`, {
                        orderId: paymentConfirmedObject.orderId,
                        status: 'paid',
                    });

                    if (response.status === 200) {
                        const referralCode = response.data?.referralCode;
                        deleteCart(cart?.restaurant_id);
                        localStorage.removeItem('restaurant_id_ref');
                        sessionStorage.removeItem('selectedRestaurant');

                        if (referralCode) {
                            sessionStorage.setItem('referralCode', referralCode);
                        }

                        navigate(`/commande/confirmation`, { replace: true });
                    } else {
                        throw new Error('Erreur lors de la mise à jour de la commande.');
                    }
                } catch (error) {
                    console.error('Erreur lors de la mise à jour de la commande:', error);
                }
            };

            const handlePaymentFailed = async () => {
                try {
                    // Confirmation du paiement et creation facture
                    const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/orders/payment/fail`, {
                        orderId: paymentConfirmedObject.orderId,
                        orderReference: paymentConfirmedObject.orderReference,
                        restaurantId: paymentConfirmedObject.restaurantId,
                        status: 'pending_payment',
                    });

                    if (response.status === 200) {
                        toast.error('Paiment refusé, un email avec les instructions de paiement vous sera envoyé', {
                            position: 'top-center',
                            autoClose: 1200,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: false,
                            draggable: true,
                            theme: 'colored',
                        });
                    } else {
                        throw new Error('Erreur lors de la mise à jour de la commande.');
                    }
                } catch (error) {
                    console.error('Erreur lors de la mise à jour de la commande:', error);
                }
            };

            switch (e.clientAnswer.orderStatus) {
                case 'PAID':
                    handlePaymentSuccess();
                    deleteAllDBs();
                    setCart(initializeCart);
                    break;

                case 'UNPAID':
                    handlePaymentFailed();
                    break;
            }
        });
    }, [amount, paymentConfirmedObject.orderId, customerEmail, orderId, restaurantId, orderReference]);

    return (
        <LargeContainer>
            <ToastContainer />

            {formToken ? (
                <div>
                    <div id="myPaymentForm" className="kr-embedded" data-kr-form-token={formToken}></div>
                    <div className="kr-pan"></div>
                    <div className="kr-expiry"></div>
                    <div className="kr-security-code"></div>
                    <button className="kr-payment-button"></button>
                    <div className="kr-form-error"></div>
                </div>
            ) : (
                <p>Chargement du formulaire de paiement...</p>
            )}
        </LargeContainer>

    );
};

export default PaymentForm;
